import { LitElement, html, unsafeCSS, nothing } from "lit";
import { property } from "lit/decorators.js";
// Styled with Interstate Tailwind utilities + the button DIY recipe (`.ids-btn` +
// data-ids-variant/size, a tailwind.config.js plugin), not the ids-* web component.
// SupportMenuTrigger.css keeps only what can't be a utility (dark mode, unread badge,
// forced-colors focus, narrow-viewport label hide).
import tailwindShared from "../../../styles/tailwind-shared.css?inline";
import tailwindComponents from "../../../styles/tailwind-components.css?inline";
import styles from "./SupportMenuTrigger.css?inline";

// P24: shared constant — imported by App.ts so aria-controls target ID never drifts
export const PANEL_ID = "support-menu-panel";

class SupportMenuTrigger extends LitElement {
  static readonly styles = [
    unsafeCSS(tailwindShared),
    unsafeCSS(tailwindComponents),
    unsafeCSS(styles),
  ];

  @property({ type: Boolean }) open = false;
  @property({ type: Boolean }) pendoMode = false;
  // US2003211 (dark-button fix): drives the dark "Get Help" pill styling. Reflected
  // to a `dark` host attribute so the shadow CSS keys off `:host([dark])`. App.ts sets
  // this from bb_theme="Interstate-Dark" REGARDLESS of showPendo — the button renders in
  // both Pendo and legacy mode, so its theme follows the page theme, not Pendo injection.
  // Self-contained in this shadow root (no document hook, no :host-context() Firefox gap).
  @property({ type: Boolean, reflect: true }) dark = false;
  @property() panelId = PANEL_ID;
  // US2017758: the active locale resolved upstream (App.ts) from bb_lang. Reflected
  // to the host `lang` attribute (see connectedCallback + updated) so assistive tech
  // switches its pronunciation dictionary for the localized label — e.g. a French
  // label is announced with French phonetics, not English (ARGUS PR #153). The visible
  // string itself is still driven by `label` (already localized by App.ts via lookupLabel).
  @property() locale = "en";
  // US2017758: reactive label driving BOTH the visible .trigger-label text AND the
  // host aria-label (the accessible name). Default "Get Help" (English) so the
  // standalone / no-binding case is unchanged.
  @property() label = "Get Help";

  override updated(changed: Map<PropertyKey, unknown>): void {
    // P13+P5: reflect ARIA state to host element so tests and AT can observe
    // without needing to pierce shadow DOM
    if (changed.has("open") || changed.has("pendoMode") || changed.size === 0) {
      this.setAttribute("aria-expanded", this.open ? "true" : "false");
      // In Pendo mode no dialog opens from this component; aria-haspopup="false"
      // avoids a misleading WCAG 4.1.2 announcement. In legacy mode the slideout
      // is a dialog and aria-haspopup="dialog" is correct.
      this.setAttribute("aria-haspopup", this.pendoMode ? "false" : "dialog");
    }
    // US2017758: keep the accessible name in lockstep with the localized label.
    // Host aria-label is authoritative (WCAG 4.1.2), so it always equals `label` —
    // immune to any aria-hidden child (e.g. the decorative SVG help icon).
    if (changed.has("label")) {
      this.setAttribute("aria-label", this.label);
    }
    // US2017758 (ARGUS PR #153): propagate the active locale to the host `lang`
    // attribute so AT switches its pronunciation voice/dictionary for the localized
    // label. `lang` is inherited by the shadow subtree (incl. .trigger-label) despite
    // :host { display: contents }.
    if (changed.has("locale")) {
      this.setAttribute("lang", this.locale);
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute("aria-haspopup", this.pendoMode ? "false" : "dialog");
    this.setAttribute("aria-expanded", this.open ? "true" : "false");
    // US2017758: the localized label is authoritative for the accessible name —
    // always reflect it (the prior set-if-absent guard is dropped). Host aria-label
    // wins over any aria-hidden child, so the accessible name == the localized label.
    this.setAttribute("aria-label", this.label);
    // US2017758 (ARGUS PR #153): reflect lang on connect for the standalone/no-binding
    // case (default "en"); updated() keeps it in lockstep on runtime locale changes.
    this.setAttribute("lang", this.locale);
    this.addEventListener("click", this._handleClick);
    this.addEventListener("keydown", this._onKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
    this.removeEventListener("keydown", this._onKeydown);
  }

  // stopPropagation prevents double-emit: the host has both a connectedCallback
  // click listener AND a @click binding on the native button. Without it, a mouse
  // click fires _handleClick from the template binding, then the event bubbles to
  // the host listener and fires it a second time.
  private _handleClick = (e?: Event): void => {
    e?.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("support-menu-trigger-toggle", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  // P23: host-level keyboard handler — Enter/Space activate the trigger;
  // preventDefault suppresses the native button's synthetic click to prevent
  // double-emit when focused on the inner <button>.
  private _onKeydown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this._handleClick();
    }
  };

  override render() {
    return html`
      <span class="trigger-wrapper relative inline-flex items-center mr-2">
        <button
          type="button"
          class="trigger-button ids-btn whitespace-nowrap"
          data-ids-variant="secondary"
          data-ids-size="xs"
          aria-haspopup=${this.pendoMode ? "false" : "dialog"}
          ?aria-expanded=${this.open}
          aria-controls=${this.pendoMode ? nothing : this.panelId}
          @click=${this._handleClick}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            width="16"
            height="16"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
            class="block"
          >
            <path
              fill-rule="evenodd"
              d="M8.00009 1.42691C4.36987 1.42691 1.427 4.36978 1.427 8C1.427 11.6302 4.36987 14.5731 8.00009 14.5731C11.6303 14.5731 14.5732 11.6302 14.5732 8C14.5732 4.36978 11.6303 1.42691 8.00009 1.42691ZM0.177002 8C0.177002 3.67943 3.67952 0.17691 8.00009 0.17691C12.3207 0.17691 15.8232 3.67943 15.8232 8C15.8232 12.3206 12.3207 15.8231 8.00009 15.8231C3.67952 15.8231 0.177002 12.3206 0.177002 8ZM9.28531 4.88639C8.58395 4.27269 7.4166 4.27269 6.71523 4.88639C6.45546 5.11369 6.06061 5.08737 5.83331 4.82759C5.60601 4.56782 5.63233 4.17297 5.8921 3.94567C7.06475 2.9196 8.93579 2.9196 10.1084 3.94567C11.33 5.01456 11.33 6.78656 10.1084 7.85545C9.9014 8.03661 9.67342 8.18496 9.43311 8.30126C8.89457 8.56189 8.62527 8.91262 8.62527 9.19968V9.79952C8.62527 10.1447 8.34545 10.4245 8.00027 10.4245C7.6551 10.4245 7.37527 10.1447 7.37527 9.79952V9.19968C7.37527 8.16161 8.23434 7.49272 8.88858 7.1761C9.03272 7.10634 9.16618 7.01897 9.28531 6.91473C9.93774 6.34385 9.93774 5.45726 9.28531 4.88639ZM7.37509 12.1989C7.37509 11.8537 7.65491 11.5739 8.00009 11.5739H8.00609C8.35127 11.5739 8.63109 11.8537 8.63109 12.1989V12.2049C8.63109 12.5501 8.35127 12.8299 8.00609 12.8299H8.00009C7.65491 12.8299 7.37509 12.5501 7.37509 12.2049V12.1989Z"
            />
          </svg>
          <span class="trigger-label">${this.label}</span>
        </button>
      </span>
    `;
  }
}

if (!customElements.get("support-menu-trigger")) {
  customElements.define("support-menu-trigger", SupportMenuTrigger);
}
